import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSelector } from "react-redux";
import vehicleService from "../../../service/vehicle.service";
import theme from "../../../theme";
import { buildVehicleListParams } from "../../../util/vehicle/vehicle-list-utils";
import {
  buildTrailerDraftFromDoc,
  emptyReboque,
  formatTrailerOptionLabel,
  normalizeTrailerIds,
} from "../../../util/vehicle/reboque-trailer-utils";
import QuickReboqueModal from "./QuickReboqueModal";

function unwrapList(response) {
  const payload = response?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

/**
 * Seção de reboques do veículo: seletor múltiplo + Novo Reboque + edição por chip.
 */
function VehicleTrailersSection({
  trailerIds,
  embeddedReboques,
  onTrailerIdsChange,
  onEmbeddedReboquesChange,
  requiresReboque,
  selectionError,
  hideCreateButton = false,
  disabled = false,
  compact = false,
}) {
  const user = useSelector((state) => state.user);
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [reboqueModalOpen, setReboqueModalOpen] = useState(false);
  const [editingReboqueId, setEditingReboqueId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const hydrateSeqRef = useRef(0);
  const hydratedIdsRef = useRef(new Set());

  const normalizedIds = useMemo(
    () => normalizeTrailerIds(trailerIds),
    [trailerIds],
  );

  const embeddedList = useMemo(
    () => (Array.isArray(embeddedReboques) ? embeddedReboques : []),
    [embeddedReboques],
  );

  const loadTrailerOptions = async () => {
    const base = buildVehicleListParams(user, {});
    if (!base) {
      setOptions([]);
      return;
    }
    setLoadingOptions(true);
    try {
      const response = await vehicleService.getAll({
        ...base,
        trailersFilter: "trailers",
        limit: 100,
      });
      setOptions(unwrapList(response));
    } catch {
      setOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    loadTrailerOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uidShipper, user?.viewALL]);

  // Sincroniza drafts a partir de options / legado
  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      const keepKeys = new Set();

      for (const id of normalizedIds) {
        keepKeys.add(id);
        const opt = options.find((o) => String(o.id) === id);
        if (opt) {
          next[id] = buildTrailerDraftFromDoc(opt);
          hydratedIdsRef.current.add(id);
        } else if (!next[id]) {
          next[id] = {
            source: "trailerId",
            data: { ...emptyReboque(), id },
            dirty: false,
            status: "loading",
          };
        } else if (
          next[id].status !== "loading" &&
          next[id].status !== "error" &&
          !hydratedIdsRef.current.has(id) &&
          !opt
        ) {
          next[id] = {
            ...next[id],
            status: "loading",
          };
        }
      }

      for (const reboque of embeddedList) {
        const id = String(reboque?.id || "").trim();
        if (!id) continue;
        keepKeys.add(id);
        next[id] = {
          source: "embedded",
          data: { ...reboque, id },
          dirty: false,
          status: "ready",
        };
      }

      for (const key of Object.keys(next)) {
        if (!keepKeys.has(key)) {
          delete next[key];
          hydratedIdsRef.current.delete(key);
        }
      }
      return next;
    });
  }, [normalizedIds, embeddedList, options]);

  // Hidrata IDs selecionados ausentes em options
  useEffect(() => {
    const missing = normalizedIds.filter((id) => {
      if (options.some((o) => String(o.id) === id)) return false;
      if (hydratedIdsRef.current.has(id)) return false;
      return true;
    });
    if (missing.length === 0) return undefined;

    const seq = ++hydrateSeqRef.current;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of missing) {
        next[id] = {
          source: "trailerId",
          data: next[id]?.data || { ...emptyReboque(), id },
          dirty: false,
          status: "loading",
        };
      }
      return next;
    });

    let cancelled = false;
    (async () => {
      try {
        const docs = await vehicleService.getByIds(missing);
        if (cancelled || seq !== hydrateSeqRef.current) return;

        const byId = new Map(
          (docs || []).map((doc) => [String(doc.id).trim(), doc]),
        );

        setOptions((prev) => {
          const merged = [...prev];
          for (const doc of docs || []) {
            if (!doc?.id) continue;
            const idx = merged.findIndex((o) => String(o.id) === String(doc.id));
            if (idx >= 0) merged[idx] = { ...merged[idx], ...doc };
            else merged.push(doc);
          }
          return merged;
        });

        setDrafts((prev) => {
          const next = { ...prev };
          for (const id of missing) {
            const doc = byId.get(id);
            if (doc) {
              next[id] = buildTrailerDraftFromDoc(doc);
              hydratedIdsRef.current.add(id);
            } else {
              next[id] = {
                source: "trailerId",
                data: { ...emptyReboque(), id },
                dirty: false,
                status: "error",
                errorMessage: "Não foi possível carregar este reboque.",
              };
            }
          }
          return next;
        });
      } catch {
        if (cancelled || seq !== hydrateSeqRef.current) return;
        setDrafts((prev) => {
          const next = { ...prev };
          for (const id of missing) {
            next[id] = {
              source: "trailerId",
              data: next[id]?.data || { ...emptyReboque(), id },
              dirty: false,
              status: "error",
              errorMessage: "Falha ao carregar o reboque selecionado.",
            };
          }
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [normalizedIds, options]);

  const selectedOptions = useMemo(() => {
    return normalizedIds.map((id) => {
      const fromOptions = options.find((o) => String(o.id) === id);
      if (fromOptions) return fromOptions;
      const draft = drafts[id]?.data;
      return {
        id,
        vehiclePlate: draft?.placaReboque || id,
        bodyworkType: draft?.TipoCarroceriaReboque || "",
        trailers: true,
      };
    });
  }, [normalizedIds, options, drafts]);

  const handleSelectionChange = (_event, values) => {
    const ids = normalizeTrailerIds((values || []).map((v) => v.id));
    onTrailerIdsChange?.(ids);
  };

  const openCreateReboqueModal = () => {
    setEditingReboqueId(null);
    setReboqueModalOpen(true);
  };

  const openEditReboqueModal = (id) => {
    const reboqueId = String(id || "").trim();
    if (!reboqueId) return;
    setEditingReboqueId(reboqueId);
    setReboqueModalOpen(true);
  };

  const closeReboqueModal = () => {
    setReboqueModalOpen(false);
    setEditingReboqueId(null);
  };

  const upsertOptionAndDraft = (doc, asForm) => {
    const id = String(doc?.id || asForm?.id || "").trim();
    if (!id) return;

    const optionDoc = doc?.id
      ? doc
      : {
          id,
          vehiclePlate: asForm?.placaReboque || "",
          bodyworkType: asForm?.TipoCarroceriaReboque || "",
          trailers: true,
        };

    setOptions((prev) => {
      if (prev.some((o) => String(o.id) === id)) {
        return prev.map((o) =>
          String(o.id) === id ? { ...o, ...optionDoc } : o,
        );
      }
      return [optionDoc, ...prev];
    });

    hydratedIdsRef.current.add(id);
    setDrafts((prev) => ({
      ...prev,
      [id]: doc?.id
        ? buildTrailerDraftFromDoc(doc)
        : {
            source: "trailerId",
            data: asForm || { ...emptyReboque(), id },
            dirty: false,
            status: "ready",
          },
    }));
  };

  const handleCreated = (created, asForm) => {
    const id = String(created?.id || "").trim();
    if (!id) return;
    upsertOptionAndDraft(created, asForm);
    onTrailerIdsChange?.(normalizeTrailerIds([...normalizedIds, id]));
  };

  const handleSaved = (savedDoc, asForm) => {
    upsertOptionAndDraft(savedDoc, asForm);
  };

  const removeEmbedded = (id) => {
    onEmbeddedReboquesChange?.(
      embeddedList.filter((r) => String(r?.id || "").trim() !== id),
    );
  };

  return (
    <Box
      sx={{
        mt: compact ? 0 : 2.5,
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      {!compact ? (
        <>
          <Typography
            sx={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 700,
              fontSize: 18,
              color: "#012443",
              mb: 0.5,
            }}
          >
            Reboques
          </Typography>
          <Typography sx={{ color: "rgba(1,36,67,0.62)", fontSize: 14, mb: 1.5 }}>
            {requiresReboque
              ? "Este tipo de veículo exige pelo menos 1 reboque vinculado."
              : "Selecione nenhum, um ou vários reboques existentes."}
          </Typography>
        </>
      ) : null}

      {selectionError ? (
        <Typography
          id="vehicle-field-trailerIds-error"
          role="alert"
          className="new-vehicle-field-error"
          sx={{ mb: 1.5 }}
          data-field-path="trailerIds"
        >
          {selectionError}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "flex-start",
          mb: compact ? 0 : 2,
        }}
      >
        <Autocomplete
          multiple
          disabled={disabled}
          sx={{ flex: 1, minWidth: compact ? 0 : 240, width: "100%" }}
          options={options}
          loading={loadingOptions}
          value={selectedOptions}
          onChange={handleSelectionChange}
          isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
          getOptionLabel={(opt) => formatTrailerOptionLabel(opt)}
          filterSelectedOptions
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, onDelete, ...tagProps } = getTagProps({ index });
              const id = String(option?.id || "").trim();
              const label = formatTrailerOptionLabel(option);
              return (
                <Chip
                  key={key}
                  {...tagProps}
                  size="small"
                  onDelete={disabled ? undefined : onDelete}
                  sx={{
                    backgroundColor: theme.colors.highlight,
                    color: theme.colors.white,
                    fontWeight: 600,
                    "& .MuiChip-deleteIcon": {
                      color: theme.colors.white,
                      "&:hover": {
                        color: theme.colors.background,
                      },
                    },
                  }}
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        maxWidth: 220,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </Box>
                      {!disabled ? (
                        <IconButton
                          size="small"
                          aria-label={`Editar reboque ${label}`}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            openEditReboqueModal(id);
                          }}
                          sx={{
                            p: 0.15,
                            color: theme.colors.white,
                            "&:hover": {
                              color: theme.colors.background,
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      ) : null}
                    </Box>
                  }
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={compact ? "" : "Reboques"}
              placeholder={
                disabled && !selectedOptions.length
                  ? "Selecione um veículo primeiro"
                  : "Buscar por placa"
              }
              size="small"
            />
          )}
        />
        {!hideCreateButton && !disabled ? (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openCreateReboqueModal}
          >
            Novo Reboque
          </Button>
        ) : null}
      </Box>

      {!compact && embeddedList.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, color: "rgba(1,36,67,0.7)", mb: 1 }}>
            Reboques legados embutidos neste veículo (não promovidos
            automaticamente):
          </Typography>
          {embeddedList.map((r) => {
            const id = String(r?.id || "").trim();
            return (
              <Box
                key={id || r.placaReboque}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 14 }}>
                  {formatTrailerOptionLabel(r)}
                </Typography>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => removeEmbedded(id)}
                >
                  Remover da seleção
                </Button>
              </Box>
            );
          })}
        </Box>
      ) : null}

      <QuickReboqueModal
        open={reboqueModalOpen}
        reboqueId={editingReboqueId}
        onClose={closeReboqueModal}
        onCreated={handleCreated}
        onSaved={handleSaved}
      />
    </Box>
  );
}

export default VehicleTrailersSection;
